import {
  defaultPosOrderDeliveryCost,
  useSetPosOrderDeliveryCost,
} from "@/shared/atoms/usePosOrderDeliveryCostAtom";
import useValidator from "@/shared/atoms/usePosOrderValidatorAtom";
import { post2 } from "@/shared/axios";
import useBranchId from "@/shared/hooks/useBranchId";
import useDiscountCodeHook from "@/shared/hooks/useDiscountCodeHook";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import useOrderInformationHook from "@/shared/hooks/useOrderInformationHook";
import { ProductStatusEnums } from "@/shared/utils/enum";
import { CartItem, CartValidatorResponse } from "@/shared/utils/interfaces";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSetCart } from "@/shared/hooks/useCartHook";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

interface ValidateCartProps {
  zipCode: string;
  deliveryMethod: number;
  discountCode?: string;
  cart?: CartItem[];
}

const useCartValidationPost = () => {
  const { t } = useTranslation();
  const branchId = useBranchId();
  const setCart = useSetCart();
  const queryClient = useQueryClient();
  const [validator, setValidator] = useValidator();
  const { discountCode, setDiscountCode } = useDiscountCodeHook();
  const setPosOrderDeliveryCost = useSetPosOrderDeliveryCost();
  const { isOrderEditing, editOrder } = useIsOrderEditingHook();
  const { isExternalOrder } = useOrderInformationHook(editOrder as OrderV2);

  const mutation = useMutation<
    CartValidatorResponse,
    AxiosError,
    ValidateCartProps
  >({
    mutationFn: ({ zipCode, deliveryMethod, discountCode, cart }) => {
      return post2(
        `internal/anonymous/websites/v1/validation/cart/${deliveryMethod}/${branchId}`,
        {
          cart: cart
            .filter((item) => {
              if (isOrderEditing) {
                return item.status_enum
                  ? item.status_enum === ProductStatusEnums.AVAILABLE
                  : true;
              }
              return true;
            })
            .map((item) => {
              return {
                name: item.name,
                vat_percentage: item.vat_percentage || 9,
                quantity: item.quantity,
                parent_id: item.parent_id > 0 ? item.parent_id : undefined,
                menu_item_id: item.item_id > 0 ? item.item_id : undefined,
                menu_item_name: item.name,
                //TODO: check if this is correct
                menu_item_price:
                  isOrderEditing && isExternalOrder ? item.price : item.price,
                sub_content: item?.selected_sub_content?.map((sc) => ({
                  name: sc.name,
                  quantity: sc.quantity,
                  parent_id: sc.parent_id > 0 ? sc.parent_id : undefined,
                  menu_item_id: sc.item_id > 0 ? sc.item_id : undefined,
                  vat_percentage: sc.vat_percentage || 9,
                  menu_item_price: sc.price,
                  menu_item_name: sc.name,
                })),
              };
            }),
          zip_code: zipCode,
          discount_code_value: discountCode,
          source: 19,
        }
      );
    },
    onSuccess: async (data) => {
      data.delivery_fee
        ? setPosOrderDeliveryCost(data.delivery_fee)
        : validator?.delivery_fee &&
          parseFloat(data.delivery_fee || "0") === 0 &&
          setPosOrderDeliveryCost(defaultPosOrderDeliveryCost);

      setValidator(data);
      const validatedItems = data.validate.flat();
      validatedItems?.length !== 0 &&
        setCart((cartItems) =>
          cartItems
            .map((cartItem) => {
              const { item_id, selected_sub_content = [] } = cartItem;
              let subContent = selected_sub_content;

              const invalidCartItem = data?.validate?.find(
                (wrongCartItem) => wrongCartItem?.[item_id]
              );

              const invalidToppingCartItem = data?.validate?.find(
                (wrongCartItem) =>
                  subContent
                    .map((s) => s.item_id)
                    .includes(Number(Object.keys(wrongCartItem || {})[0]))
              );

              if (invalidCartItem !== undefined) {
                queryClient.refetchQueries({ queryKey: ["cat", branchId] });
                const values = invalidCartItem[item_id];

                cartItem = validateItem(cartItem, values);
              } else if (invalidToppingCartItem !== undefined) {
                const [id, values] = Object.entries(invalidToppingCartItem)[0];

                subContent = selected_sub_content.map((s) =>
                  s.item_id === Number(id) ? validateItem(s, values) : s
                );
              }

              return cartItem !== undefined
                ? { ...cartItem, selected_sub_content: subContent }
                : undefined;
            })
            .filter(Boolean)
        );
    },
    onSettled: () => {
      setDiscountCode({ ...discountCode, isApplied: false });
    },
  });

  const validateItem = (cartItem, values) => {
    if ("parent_id_does_not_exist" in values) {
      toast.warning(t("validator.idDoesNotExist"));
      cartItem = undefined;
    }
    if ("id_does_not_exist" in values) {
      toast.warning(t("validator.idDoesNotExist"));
      cartItem = undefined;
    }
    if ("price" in values) {
      cartItem = {
        ...cartItem,
        price: String(parseFloat(values.price)),
      };
    }
    if ("name" in values) {
      cartItem = { ...cartItem, name: values.name };
    }
    if ("is_active" in values) {
      cartItem = undefined;
    }
    if ("status" in values) {
      cartItem = undefined;
    }
    if ("target_mismatch" in values) {
      toast.warning(t("validator.targetMismatch", { name: cartItem.name }), {
        autoClose: 5000,
      });
      cartItem = undefined;
    }
    return cartItem;
  };

  return mutation;
};

export default useCartValidationPost;
